import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import type { ProjectListItem, ProjectStatus } from "@/mocks/admin/projects";
import type { Booking, BookingStage } from "@/mocks/admin/bookings";
import type { CrewMember, CrewStatus, ShiftEntry } from "@/mocks/admin/crew";
import type { Customer, CustomerStatus, CustomerType } from "@/mocks/admin/customers";
import type { Payment, PaymentStatus, PaymentType } from "@/mocks/admin/payments";
import type { Feedback } from "@/mocks/admin/feedback";

// ---------- PROJECTS ----------

function rowToProject(row: Record<string, unknown>): ProjectListItem {
  return {
    id: row.id as string,
    customer: (row.customer_name as string) || "",
    customerInitials: (row.customer_initials as string) || "",
    customerColor: (row.customer_color as string) || "bg-ink-500",
    moveType: (row.move_type as string) || "",
    date: (row.move_date as string) || "",
    time: (row.move_time as string) || "",
    fromCity: (row.from_city as string) || "",
    toCity: (row.to_city as string) || "",
    truck: (row.truck as string) || "",
    crewCount: (row.crew_count as number) || 0,
    leadCrew: (row.lead_crew as string) || "",
    status: (row.status as ProjectStatus) || "scheduled",
    total: Number(row.total) || 0,
    depositPaid: !!row.deposit_paid,
  };
}

export function useProjects() {
  const [data, setData] = useState<ProjectListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: rows, error: err } = await supabase
        .from("moving_projects")
        .select("*")
        .order("created_at", { ascending: false });
      if (err) {
        setError(err.message);
      } else {
        setData((rows || []).map(rowToProject));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to fetch projects");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

export async function fetchProject(id: string) {
  const { data, error } = await supabase
    .from("moving_projects")
    .select("*")
    .eq("id", id)
    .maybeSingle();
  if (error) throw error;
  return data ? rowToProject(data) : null;
}

export async function fetchProjectRaw(id: string) {
  const { data, error } = await supabase
    .from("moving_projects")
    .select("*")
    .eq("id", id)
    .maybeSingle();
  if (error) throw error;
  return data;
}

// ---------- BOOKINGS ----------

function rowToBooking(row: Record<string, unknown>): Booking {
  return {
    id: row.id as string,
    customer: (row.customer as string) || "",
    initials: (row.initials as string) || "",
    color: (row.color as string) || "bg-ink-500",
    email: (row.email as string) || "",
    phone: (row.phone as string) || "",
    moveType: (row.move_type as string) || "",
    preferredDate: (row.preferred_date as string) || "",
    preferredTime: (row.preferred_time as string) || "",
    fromCity: (row.from_city as string) || "",
    toCity: (row.to_city as string) || "",
    rooms: (row.rooms as string) || "",
    estimatedQuote: row.estimated_quote === null ? null : Number(row.estimated_quote),
    depositRequired: row.deposit_required === null ? null : Number(row.deposit_required),
    depositPaid: !!row.deposit_paid,
    stage: (row.stage as BookingStage) || "new",
    source: (row.source as string) || "",
    submittedAt: (row.submitted_at as string) || "",
    notes: (row.notes as string) || "",
  };
}

export function useBookings() {
  const [data, setData] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: rows, error: err } = await supabase
        .from("bookings")
        .select("*")
        .order("created_at", { ascending: false });
      if (err) {
        setError(err.message);
      } else {
        setData((rows || []).map(rowToBooking));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to fetch bookings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

// ---------- CREW ----------

function rowToCrew(row: Record<string, unknown>): CrewMember {
  return {
    id: row.id as string,
    name: (row.name as string) || "",
    initials: (row.initials as string) || "",
    color: (row.color as string) || "bg-ink-500",
    role: (row.role as string) || "",
    email: (row.email as string) || "",
    phone: (row.phone as string) || "",
    hireDate: (row.hire_date as string) || "",
    rating: Number(row.rating) || 0,
    completedMoves: (row.completed_moves as number) || 0,
    hourlyRate: Number(row.hourly_rate) || 0,
    weeklyHours: Number(row.weekly_hours) || 0,
    weeklyTarget: Number(row.weekly_target) || 40,
    status: (row.status as CrewStatus) || "off",
    currentProject: (row.current_project as string) || undefined,
    clockedInAt: (row.clocked_in_at as string) || undefined,
    skills: (row.skills as string[]) || [],
    availability: (row.availability as CrewMember["availability"]) || [],
    city: (row.city as string) || "",
    accessToken: (row.access_token as string) || undefined,
  };
}

export async function fetchCrewMemberByToken(token: string) {
  const { data, error } = await supabase
    .from("crew_members")
    .select("*")
    .eq("access_token", token)
    .maybeSingle();
  if (error) throw error;
  return data ? rowToCrew(data) : null;
}

export function useCrew() {
  const [data, setData] = useState<CrewMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: rows, error: err } = await supabase
        .from("crew_members")
        .select("*")
        .order("name", { ascending: true });
      if (err) {
        setError(err.message);
      } else {
        setData((rows || []).map(rowToCrew));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to fetch crew");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

export async function fetchCrewMember(id: string) {
  const { data, error } = await supabase
    .from("crew_members")
    .select("*")
    .eq("id", id)
    .maybeSingle();
  if (error) throw error;
  return data ? rowToCrew(data) : null;
}

// ---------- CUSTOMERS ----------

function rowToCustomer(row: Record<string, unknown>): Customer {
  return {
    id: row.id as string,
    name: (row.name as string) || "",
    initials: (row.initials as string) || "",
    color: (row.color as string) || "bg-ink-500",
    type: (row.type as CustomerType) || "individual",
    status: (row.status as CustomerStatus) || "active",
    email: (row.email as string) || "",
    phone: (row.phone as string) || "",
    city: (row.city as string) || "",
    state: (row.state as string) || "",
    source: (row.source as string) || "",
    totalMoves: (row.total_moves as number) || 0,
    lifetimeValue: Number(row.lifetime_value) || 0,
    lastMoveDate: (row.last_move_date as string) || "",
    lastMoveRelative: (row.last_move_relative as string) || "",
    rating: row.rating === null ? null : (row.rating as number),
    tags: (row.tags as string[]) || [],
    nextBookingDate: (row.next_booking_date as string) || null,
  };
}

export function useCustomers() {
  const [data, setData] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: rows, error: err } = await supabase
        .from("customers")
        .select("*")
        .order("name", { ascending: true });
      if (err) {
        setError(err.message);
      } else {
        setData((rows || []).map(rowToCustomer));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to fetch customers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

// ---------- SHIFTS ----------

function rowToShift(row: Record<string, unknown>): ShiftEntry {
  return {
    id: row.id as string,
    crewId: (row.crew_id as string) || "",
    crewName: (row.crew_name as string) || "",
    initials: (row.initials as string) || "",
    color: (row.color as string) || "bg-ink-500",
    role: (row.role as string) || "",
    projectId: (row.project_id as string) || "",
    clockIn: (row.clock_in as string) || "",
    clockOut: (row.clock_out as string) || null,
    break: (row.break_minutes as number) || 0,
    totalHours: row.total_hours === null ? null : Number(row.total_hours),
    status: (row.status as ShiftEntry["status"]) || "active",
    hourlyRate: Number(row.hourly_rate) || 0,
  };
}

export function useShifts() {
  const [active, setActive] = useState<ShiftEntry[]>([]);
  const [pending, setPending] = useState<
    {
      id: string;
      crewId: string;
      crewName: string;
      initials: string;
      color: string;
      projectId: string;
      date: string;
      clockIn: string;
      clockOut: string;
      hours: number;
      flag?: string;
    }[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: rows, error: err } = await supabase
        .from("shifts")
        .select("*")
        .order("clock_in", { ascending: true });
      if (err) {
        setError(err.message);
      } else {
        const all = (rows || []).map((r) => ({ ...r, _shift: rowToShift(r) }));
        setActive(
          all
            .filter((r) => r.status === "active" || r.status === "on-break")
            .map((r) => r._shift),
        );
        setPending(
          all
            .filter((r) => r.status === "pending-approval")
            .map((r) => ({
              id: r.id as string,
              crewId: (r.crew_id as string) || "",
              crewName: (r.crew_name as string) || "",
              initials: (r.initials as string) || "",
              color: (r.color as string) || "bg-ink-500",
              projectId: (r.project_id as string) || "",
              date: (r.shift_date as string) || "",
              clockIn: (r.clock_in as string) || "",
              clockOut: (r.clock_out as string) || "",
              hours: Number(r.total_hours) || 0,
              flag: (r.flag as string) || undefined,
            })),
        );
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to fetch shifts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { active, pending, loading, error, refetch: fetchData };
}

// ---------- TIMECLOCK SUMMARY ----------

const MONTHS: Record<string, number> = {
  Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
  Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
};

function parseShiftDate(str: string): Date | null {
  if (!str) return null;
  const parts = str.trim().split(/\s+/);
  if (parts.length < 2) return null;
  const m = MONTHS[parts[0] as keyof typeof MONTHS];
  const d = parseInt(parts[1], 10);
  if (m === undefined || isNaN(d)) return null;
  return new Date(2026, m, d);
}

function formatShiftDateLabel(d: Date): string {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[d.getMonth()]} ${String(d.getDate()).padStart(2, "0")}`;
}

type ShiftRow = {
  id: string;
  crew_id: string;
  crew_name: string;
  initials: string;
  color: string;
  role: string;
  project_id: string;
  clock_in: string;
  clock_out: string | null;
  break_minutes: number;
  total_hours: number | null;
  status: string;
  hourly_rate: number;
  shift_date: string;
};

export type TimeclockStats = {
  onClock: number;
  avgTodayHours: number;
  laborTodayCents: number;
  todayLabel: string;
};

export function useTimeclockStats() {
  const [stats, setStats] = useState<TimeclockStats>({
    onClock: 0,
    avgTodayHours: 0,
    laborTodayCents: 0,
    todayLabel: "",
  });
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const { data: rows } = await supabase.from("shifts").select("*");
    const list = (rows || []) as ShiftRow[];

    // Determine "today" = the most recent shift_date present in the data
    const distinctDates = Array.from(
      new Set(list.map((r) => r.shift_date).filter(Boolean)),
    )
      .map((s) => ({ raw: s, d: parseShiftDate(s) }))
      .filter((x): x is { raw: string; d: Date } => !!x.d)
      .sort((a, b) => b.d.getTime() - a.d.getTime());
    const todayLabel = distinctDates[0]?.raw || "";

    const onClock = list.filter(
      (r) => r.status === "active" || r.status === "on-break",
    ).length;

    const todayShifts = list.filter((r) => r.shift_date === todayLabel);
    const completedToday = todayShifts.filter(
      (r) => r.total_hours !== null && r.total_hours !== undefined,
    );

    const totalHoursToday = completedToday.reduce(
      (sum, r) => sum + Number(r.total_hours || 0),
      0,
    );
    const avgTodayHours =
      completedToday.length > 0 ? totalHoursToday / completedToday.length : 0;

    const laborTodayCents = Math.round(
      todayShifts.reduce(
        (sum, r) =>
          sum + Number(r.total_hours || 0) * Number(r.hourly_rate || 0) * 100,
        0,
      ),
    );

    setStats({ onClock, avgTodayHours, laborTodayCents, todayLabel });
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { ...stats, loading, refetch: fetchData };
}

// ---------- WEEKLY TIMESHEETS ----------

export type WeeklyTimesheetRow = {
  crewId: string;
  name: string;
  initials: string;
  color: string;
  role: string;
  hours: number;
  regularHours: number;
  otHours: number;
  pay: number;
  shifts: number;
};

export type WeeklyTimesheets = {
  rows: WeeklyTimesheetRow[];
  weekStartLabel: string;
  weekEndLabel: string;
  totalHours: number;
  totalOT: number;
  totalPay: number;
};

export function useWeeklyTimesheets() {
  const [data, setData] = useState<WeeklyTimesheets>({
    rows: [],
    weekStartLabel: "",
    weekEndLabel: "",
    totalHours: 0,
    totalOT: 0,
    totalPay: 0,
  });
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const { data: rows } = await supabase.from("shifts").select("*");
    const list = (rows || []) as ShiftRow[];

    // Take the 7 most recent distinct shift_dates as "the current week window"
    const parsed = list
      .map((r) => ({ ...r, _d: parseShiftDate(r.shift_date) }))
      .filter((x): x is ShiftRow & { _d: Date } => !!x._d);

    const distinct = Array.from(
      new Map(parsed.map((p) => [p.shift_date, p._d])).entries(),
    )
      .map(([raw, d]) => ({ raw, d }))
      .sort((a, b) => b.d.getTime() - a.d.getTime())
      .slice(0, 7);

    const weekEnd = distinct[0]?.d;
    const weekStart = distinct[distinct.length - 1]?.d;
    const weekDates = new Set(distinct.map((x) => x.raw));

    // Only count shifts that are completed (have total_hours) within the week
    const weekShifts = parsed.filter(
      (r) =>
        weekDates.has(r.shift_date) &&
        r.total_hours !== null &&
        r.total_hours !== undefined,
    );

    // Group by crew
    const byCrew = new Map<string, ShiftRow[]>();
    weekShifts.forEach((r) => {
      const arr = byCrew.get(r.crew_id) || [];
      arr.push(r);
      byCrew.set(r.crew_id, arr);
    });

    const aggregated: WeeklyTimesheetRow[] = Array.from(byCrew.entries()).map(
      ([crewId, items]) => {
        const totalHrs = items.reduce(
          (s, r) => s + Number(r.total_hours || 0),
          0,
        );
        const regularHours = Math.min(40, totalHrs);
        const otHours = Math.max(0, totalHrs - 40);
        const pay = items.reduce(
          (s, r) =>
            s + Number(r.total_hours || 0) * Number(r.hourly_rate || 0),
          0,
        );
        const first = items[0];
        return {
          crewId,
          name: first.crew_name || "",
          initials: first.initials || "",
          color: first.color || "bg-ink-500",
          role: first.role || "",
          hours: Number(totalHrs.toFixed(2)),
          regularHours: Number(regularHours.toFixed(2)),
          otHours: Number(otHours.toFixed(2)),
          pay: Math.round(pay),
          shifts: items.length,
        };
      },
    );

    aggregated.sort((a, b) => b.hours - a.hours);

    const totalHours = aggregated.reduce((s, r) => s + r.hours, 0);
    const totalOT = aggregated.reduce((s, r) => s + r.otHours, 0);
    const totalPay = aggregated.reduce((s, r) => s + r.pay, 0);

    setData({
      rows: aggregated,
      weekStartLabel: weekStart ? formatShiftDateLabel(weekStart) : "",
      weekEndLabel: weekEnd ? formatShiftDateLabel(weekEnd) : "",
      totalHours: Number(totalHours.toFixed(1)),
      totalOT: Number(totalOT.toFixed(1)),
      totalPay: Math.round(totalPay),
    });
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { ...data, loading, refetch: fetchData };
}

// ---------- PAYMENTS ----------

function rowToPayment(row: Record<string, unknown>): Payment {
  return {
    id: row.id as string,
    projectId: (row.project_id as string) || "",
    customer: (row.customer as string) || "",
    initials: (row.initials as string) || "",
    color: (row.color as string) || "bg-ink-500",
    amount: Number(row.amount) || 0,
    type: (row.type as PaymentType) || "deposit",
    method: (row.method as string) || "",
    methodIcon: (row.method_icon as string) || "",
    last4: (row.last4 as string) || "",
    status: (row.status as PaymentStatus) || "succeeded",
    date: (row.date as string) || "",
    fee: Number(row.fee) || 0,
  };
}

export function usePayments() {
  const [data, setData] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: rows, error: err } = await supabase
        .from("admin_payments")
        .select("*")
        .order("created_at", { ascending: false });
      if (err) {
        setError(err.message);
      } else {
        setData((rows || []).map(rowToPayment));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to fetch payments");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

// ---------- FEEDBACK ----------

function rowToFeedback(row: Record<string, unknown>): Feedback {
  return {
    id: row.id as string,
    customer: (row.customer as string) || "",
    initials: (row.initials as string) || "",
    color: (row.color as string) || "bg-ink-500",
    projectId: (row.project_id as string) || "",
    moveType: (row.move_type as string) || "",
    rating: (row.rating as number) || 0,
    title: (row.title as string) || "",
    body: (row.body as string) || "",
    date: (row.date as string) || "",
    source: (row.source as Feedback["source"]) || "Email Survey",
    status: (row.status as Feedback["status"]) || "new",
    crewMentioned: (row.crew_mentioned as Feedback["crewMentioned"]) || [],
    reply: (row.reply as Feedback["reply"]) || undefined,
  };
}

export function useFeedback() {
  const [data, setData] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: rows, error: err } = await supabase
        .from("feedback")
        .select("*")
        .order("created_at", { ascending: false });
      if (err) {
        setError(err.message);
      } else {
        setData((rows || []).map(rowToFeedback));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to fetch feedback");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}